<template>
  <app-layout>
    <div class="sc-container mc-page">

      <div class="mc-header">
        <div>
          <h1 class="mc-title">My Companies</h1>
          <p class="mc-sub">Companies you have registered on Structurecity</p>
        </div>
        <router-link to="/register-company" class="mc-btn mc-btn--primary">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Register a Company
        </router-link>
      </div>

      <div v-if="loading" class="mc-loading">
        <div class="mc-spinner"></div>
      </div>

      <div v-else-if="companies.length === 0" class="mc-empty">
        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
        <p>You haven't registered any companies yet.</p>
        <router-link to="/register-company" class="mc-btn mc-btn--primary">Register your first company</router-link>
      </div>

      <div v-else class="mc-grid">
        <router-link
          v-for="co in companies"
          :key="co.id"
          :to="'/company/' + co.slug"
          class="mc-card"
        >
          <div class="mc-card__logo-wrap">
            <img
              v-if="co.logo"
              :src="imageUrl + 'users/' + co.logo"
              class="mc-card__logo"
              :alt="co.name"
              @error="$event.target.src = '/assets/avatar.png'"
            />
            <img v-else :src="initAvatar(co.name)" class="mc-card__logo" :alt="co.name" />
          </div>
          <div class="mc-card__body">
            <h2 class="mc-card__name">{{ co.name }}</h2>
            <p v-if="co.industry" class="mc-card__industry">{{ co.industry }}</p>
            <p v-if="co.short_description" class="mc-card__desc" v-html="co.short_description"></p>
            <div class="mc-card__meta">
              <span v-if="co.city">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                {{ [co.city && co.city.name, co.state && co.state.name].filter(Boolean).join(', ') }}
              </span>
              <span v-if="co.company_size">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                {{ co.company_size }}
              </span>
            </div>
          </div>
          <div class="mc-card__arrow">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
        </router-link>
      </div>

    </div>
  </app-layout>
</template>

<script>
import AppLayout from '@/layouts/AppLayout';
import axios from 'axios';

export default {
  name: 'MyCompanies',
  components: { AppLayout },

  data() {
    return {
      loading: true,
      companies: [],
      imageUrl: process.env.VUE_APP_IMAGE_URL || '',
    };
  },

  created() {
    this.fetchCompanies();
  },

  methods: {
    nameColor(name) {
      const palette = ['#4f46e5','#0891b2','#059669','#d97706','#7c3aed','#db2777','#ea580c','#0284c7'];
      let h = 0;
      for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
      return palette[Math.abs(h) % palette.length];
    },
    initAvatar(name) {
      const initials = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
      const bg = this.nameColor(name);
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56"><rect width="56" height="56" rx="10" fill="${bg}"/><text x="28" y="36" font-family="Arial,sans-serif" font-size="20" font-weight="700" fill="#fff" text-anchor="middle">${initials}</text></svg>`;
      return 'data:image/svg+xml;base64,' + btoa(svg);
    },
    fetchCompanies() {
      const userId = localStorage.getItem('user');
      if (!userId) { this.loading = false; return; }
      axios(process.env.VUE_APP_API_URL + 'user/companies/' + userId)
        .then(res => {
          if (res.data && res.data.data) this.companies = res.data.data;
          else if (Array.isArray(res.data)) this.companies = res.data;
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },
  },
};
</script>

<style scoped>
.mc-page { padding: 40px 16px 64px; }

.mc-header {
  display: flex; align-items: center; justify-content: space-between;
  gap: 1rem; margin-bottom: 32px; flex-wrap: wrap;
}
.mc-title { font-size: 1.5rem; font-weight: 700; color: var(--text, #111827); margin: 0 0 4px; }
.mc-sub { color: var(--text-muted, #6b7280); font-size: .9375rem; margin: 0; }

/* Buttons */
.mc-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 18px; border-radius: 8px; font-size: .875rem;
  font-weight: 600; cursor: pointer; text-decoration: none;
  transition: all .18s; border: none;
}
.mc-btn--primary { background: var(--primary, #1e3a5f); color: #fff; }
.mc-btn--primary:hover { filter: brightness(1.1); }

/* Loading */
.mc-loading { display: flex; justify-content: center; padding: 64px 0; }
.mc-spinner {
  width: 36px; height: 36px; border-radius: 50%;
  border: 3px solid var(--border-light, #e5e7eb);
  border-top-color: var(--primary, #1e3a5f);
  animation: mc-spin .7s linear infinite;
}
@keyframes mc-spin { to { transform: rotate(360deg); } }

/* Empty state */
.mc-empty {
  text-align: center; padding: 64px 16px;
  display: flex; flex-direction: column; align-items: center; gap: 16px;
  color: var(--text-muted, #9ca3af);
}
.mc-empty p { font-size: .9375rem; margin: 0; }

/* Grid */
.mc-grid { display: flex; flex-direction: column; gap: 12px; }

/* Card */
.mc-card {
  display: flex; align-items: center; gap: 16px;
  padding: 20px; border-radius: var(--radius, 10px);
  border: 1px solid var(--border-light, #e5e7eb);
  background: var(--surface, #fff);
  text-decoration: none; transition: box-shadow .2s, border-color .2s;
}
.mc-card:hover { border-color: var(--primary, #1e3a5f); box-shadow: var(--shadow, 0 2px 8px rgba(0,0,0,.08)); }

.mc-card__logo-wrap { flex-shrink: 0; }
.mc-card__logo { width: 56px; height: 56px; border-radius: 10px; object-fit: cover; border: 1px solid var(--border-light, #e5e7eb); }
.mc-card__body { flex: 1; min-width: 0; }
.mc-card__name { font-size: 1rem; font-weight: 700; color: var(--text, #111827); margin: 0 0 2px; }
.mc-card__industry { font-size: .8125rem; color: var(--primary, #1e3a5f); margin: 0 0 4px; font-weight: 500; }
.mc-card__desc {
  font-size: .875rem; color: var(--text-muted, #6b7280); margin: 0 0 8px;
  overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
}
.mc-card__meta {
  display: flex; flex-wrap: wrap; gap: 12px;
  font-size: .8rem; color: var(--text-muted, #9ca3af);
}
.mc-card__meta span { display: flex; align-items: center; gap: 4px; }

.mc-card__arrow { flex-shrink: 0; color: var(--text-muted, #d1d5db); }
.mc-card:hover .mc-card__arrow { color: var(--primary, #1e3a5f); }

@media (max-width: 480px) {
  .mc-card__arrow { display: none; }
}
</style>
